import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { PresentationDetail, PresentationItemInput, PresentationSummary, SetlistSong, WorshipService } from '../../shared/api/types';
import { Alert, Button, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { DeviceCard } from './DeviceCard';
import { orderParts, rememberPassages } from './orderPassages';

export const serviceWhen = (iso: string) => new Date(iso).toLocaleString('es', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

type SermonProgram = { passages: string[]; materials?: { id: string; name: string }[] };

const item = (i: Pick<PresentationItemInput, 'kind' | 'title'> & Partial<PresentationItemInput>): PresentationItemInput => ({
  body: null,
  passage: null,
  resourceId: null,
  stageNotes: null,
  arrangement: null,
  backgroundPreset: null,
  localFile: null,
  ...i,
  title: i.title.trim().slice(0, 120) || 'Elemento',
});

/**
 * Los cultos programados (de hoy en adelante) para elegir cuál se proyecta. Si el culto ya tiene su
 * presentación se abre; si no, se arma sola con los cantos que eligió Alabanza y los versículos y el
 * material de la Predicación, y se abre lista para proyectar.
 */
function ScheduledServices({ churchId, presentations, canOperate }: { churchId: string; presentations: PresentationSummary[]; canOperate: boolean }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const services = useAsync(
    (signal) =>
      api
        .get<{ items: WorshipService[] }>(`/worship/churches/${churchId}/services?from=${encodeURIComponent(new Date(Date.now() - 6 * 3_600_000).toISOString())}`, signal)
        .then((r) => r.items.filter((s) => s.status !== 'canceled').sort((a, b) => a.startsAt.localeCompare(b.startsAt)).slice(0, 15)),
    [churchId],
  );

  async function prepare(s: WorshipService) {
    setBusy(s.id);
    setError(null);
    try {
      const base = `/worship/churches/${churchId}/services/${s.id}`;
      const [songs, program, parts] = await Promise.all([
        api.get<{ items: SetlistSong[] }>(`${base}/songs`).then((r) => r.items).catch(() => [] as SetlistSong[]),
        api.get<SermonProgram>(`${base}/program`).catch(() => null),
        orderParts(churchId, s.id),
      ]);
      const songItems = songs.map((x) => item({ kind: 'lyrics', title: x.title, body: x.lyrics.trim() || null }));
      const sermonItems = [
        ...(program?.passages ?? []).map((passage) => item({ kind: 'scripture', title: passage, passage })),
        ...(program?.materials ?? []).map((m) => item({ kind: 'media', title: m.name.replace(/\.[^.]+$/, ''), resourceId: m.id })),
      ];
      // En el orden del culto: los cantos en Alabanza, la Predicación en su lugar y los pasajes de cada parte.
      const items: PresentationItemInput[] = [];
      let songsIn = false;
      let sermonIn = false;
      for (const part of parts) {
        if (part.ministry?.systemKey === 'alabanza' && !songsIn) {
          items.push(...songItems);
          songsIn = true;
        }
        if (part.link === 'predicacion' && !sermonIn) {
          items.push(...sermonItems);
          sermonIn = true;
        }
        for (const ps of part.passages ?? []) items.push(item({ kind: 'scripture', title: `${ps.passage}${ps.translation ? ` (${ps.translation})` : ''}`, passage: ps.passage, translation: ps.translation }));
      }
      if (!songsIn) items.unshift(...songItems);
      if (!sermonIn) items.push(...sermonItems);
      const title = s.title.trim().length >= 3 ? s.title.trim().slice(0, 120) : `Culto ${s.title.trim()}`.trim();
      const saved = await api.post<PresentationDetail>(`/presentation/churches/${churchId}/presentations`, { title, serviceId: s.id, items });
      rememberPassages(saved.id, parts.flatMap((part) => (part.passages ?? []).map((ps) => ps.id)));
      navigate(`/app/presentaciones/${saved.id}`);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(null);
    }
  }

  if (services.status === 'loading') return <Loading />;
  if (services.status === 'error') return <ErrorState error={services.error} onRetry={services.reload} />;
  return (
    <section className="services-pick" aria-label="Cultos programados">
      <h2>Cultos programados</h2>
      {error ? <Alert>{error}</Alert> : null}
      {services.data.length === 0 ? <p className="muted">No hay cultos programados de hoy en adelante. Se programan en Culto (desde una cuenta con permiso).</p> : null}
      {services.data.length > 0 ? (
        <ul className="pick-tiles">
          {services.data.map((s) => {
            const linked = presentations.find((p) => p.service?.id === s.id);
            const when = serviceWhen(s.startsAt);
            return (
              <li key={s.id} className="pick-tile">
                {linked ? (
                  <Link className="pick-tile__link" to={`/app/presentaciones/${linked.id}`}>
                    <strong>{s.title}</strong>
                    <span className="muted small">
                      {when} · {linked.items} {linked.items === 1 ? 'elemento' : 'elementos'} · Proyectar
                    </span>
                  </Link>
                ) : (
                  <button type="button" className="pick-tile__link services-pick__new" disabled={!canOperate || busy !== null} onClick={() => void prepare(s)}>
                    <strong>{s.title}</strong>
                    <span className="muted small">{busy === s.id ? 'Preparando con los cantos y la Predicación…' : `${when} · Preparar y proyectar`}</span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

/** Un culto ya pasó si empezó hace más de 6 horas (igual que «Cultos programados»). */
const PAST_MS = 6 * 3_600_000;
export const isPastService = (p: PresentationSummary, now = Date.now()) => Boolean(p.service && new Date(p.service.startsAt).getTime() < now - PAST_MS);

/**
 * «Todas las presentaciones» (#55): primero las de cultos que vienen (la más próxima arriba) y las
 * sueltas; las de cultos que ya pasaron, juntas en un renglón plegado. El servidor archiva solo las
 * de cultos de hace más de una semana (#54).
 */
export function splitByService(list: PresentationSummary[], now = Date.now()) {
  const past = list.filter((p) => isPastService(p, now)).sort((a, b) => (b.service?.startsAt ?? '').localeCompare(a.service?.startsAt ?? ''));
  const upcoming = list
    .filter((p) => !isPastService(p, now))
    .sort((a, b) => (a.service && b.service ? a.service.startsAt.localeCompare(b.service.startsAt) : a.service ? -1 : b.service ? 1 : b.updatedAt.localeCompare(a.updatedAt)));
  return { upcoming, past };
}

function PresentationTile({ churchId, p, canOperate, onChange }: { churchId: string; p: PresentationSummary; canOperate: boolean; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/presentation/churches/${churchId}/presentations/${p.id}/${p.archived ? 'restore' : 'archive'}`, {});
      onChange();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }
  return (
    <li className="pick-tile pick-tile--action">
      <Link className="pick-tile__link" to={`/app/presentaciones/${p.id}`}>
        <strong>{p.title}</strong>
        <span className="muted small">
          {p.items} {p.items === 1 ? 'elemento' : 'elementos'}
          {p.service ? ` · ${p.service.title}, ${serviceWhen(p.service.startsAt)}` : ''}
        </span>
      </Link>
      {canOperate ? (
        <Button variant="ghost" busy={busy} aria-label={`${p.archived ? 'Restaurar' : 'Archivar'} «${p.title}»`} onClick={() => void toggle()}>
          {p.archived ? 'Restaurar' : 'Archivar'}
        </Button>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </li>
  );
}

/** Motor 16, Presentación: lo que se proyecta en cada culto. En la app KEORA Multimedia se llama «Cultos». */
export function PresentationsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canOperate = me.can('presentation.operate');
  const canSeeServices = me.can('worship.read');
  const [params, setParams] = useSearchParams();
  const archived = canOperate && params.get('vista') === 'archivadas';
  const list = useAsync(
    (signal) => (churchId ? api.get<{ items: PresentationSummary[] }>(`/presentation/churches/${churchId}/presentations?archived=${archived}`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId, archived],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page presentations">
      <header className="page__header">
        <h1>{me.isDevice ? 'Cultos' : 'Proyectar'}</h1>
        <p className="muted">Elige el culto que se va a proyectar: cantos, lecturas, avisos e imágenes, con vista de escenario para el equipo.</p>
      </header>
      {/* En pantallas anchas, los cultos programados y todas las presentaciones van lado a lado. */}
      <div className="presentations__cols">
        {canSeeServices && !archived && list.status === 'ready' ? <ScheduledServices churchId={churchId} presentations={list.data} canOperate={canOperate} /> : null}
        <section className="presentations__all" aria-label="Todas las presentaciones">
          {canSeeServices && !archived ? <h2>Todas las presentaciones</h2> : null}
          {canOperate ? (
            <div className="presentations__bar">
              <div className="tabs" role="tablist" aria-label="Presentaciones">
                <button type="button" role="tab" aria-selected={!archived} className={`tabs__tab${!archived ? ' tabs__tab--active' : ''}`} onClick={() => setParams({})}>
                  Vigentes
                </button>
                <button type="button" role="tab" aria-selected={archived} className={`tabs__tab${archived ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ vista: 'archivadas' })}>
                  Archivadas
                </button>
              </div>
              {!archived ? (
                <div className="actions">
                  <Link className="btn btn--primary" to="/app/presentaciones/nueva">
                    <span>Nueva presentación</span>
                  </Link>
                  {me.isDevice ? null : (
                    <Link className="btn btn--secondary" to="/app/presentaciones/tema">
                      <span>Tema de las pantallas</span>
                    </Link>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
          {list.status === 'loading' ? <Loading /> : null}
          {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
          {list.status === 'ready' && list.data.length === 0 ? <EmptyState title={archived ? 'No hay presentaciones archivadas.' : 'Aún no hay presentaciones.'} /> : null}
          {list.status === 'ready' && list.data.length > 0 && archived ? (
            <ul className="pick-tiles">
              {list.data.map((p) => (
                <PresentationTile key={p.id} churchId={churchId} p={p} canOperate={canOperate} onChange={list.reload} />
              ))}
            </ul>
          ) : null}
          {list.status === 'ready' && list.data.length > 0 && !archived
            ? (() => {
                const { upcoming, past } = splitByService(list.data);
                return (
                  <>
                    {upcoming.length > 0 ? (
                      <ul className="pick-tiles">
                        {upcoming.map((p) => (
                          <PresentationTile key={p.id} churchId={churchId} p={p} canOperate={canOperate} onChange={list.reload} />
                        ))}
                      </ul>
                    ) : null}
                    {past.length > 0 ? (
                      <details className="presentations__past">
                        <summary>Cultos pasados ({past.length})</summary>
                        <p className="muted small">Se archivan solas una semana después del culto; las encuentras en «Archivadas» para restaurarlas.</p>
                        <ul className="pick-tiles">
                          {past.map((p) => (
                            <PresentationTile key={p.id} churchId={churchId} p={p} canOperate={canOperate} onChange={list.reload} />
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </>
                );
              })()
            : null}
        </section>
      </div>
      {canOperate && !archived ? <DeviceCard /> : null}
    </div>
  );
}
